import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import selectAnalytics from './selectAnalytics';
import Purchase from '../Product/Purchase';
import Price from '../Product/Price';
import Button from '../Elements/Button/Button';
import LazyImage from '../../hocs/Lazy/LazyImage';
import { preSelectSize, updateListItemCache } from '../../actions/checkout';
import { height } from '../../utils/dom';
import { productImageSizeTranslation } from '../../utils/image';
import { translate, prefix, removeLanguagePrefix } from '../../utils/locale';
import { blackOrWhite, rootUrl } from '../../utils/string';
import s from './ProductItem.scss';

const ProductItem = ({ content, productCache, setup, currentPath, currentView, canonical, variation = '', productPicker, itemIndex = 1, toggleProductItem, productItemExpanded, productItemHeight, expandedDirection, productLayout, ...props }) => {
	const purchaseContainer = useRef(null);
	const [lazyLoaded, toggleLazyLoad] = useState(false);
	const [quickShopHeight, updateQuickShopHeight] = useState('0');
	const [cardHeight, setCardHeight] = useState(['', 'auto']);
	const timerRef = useRef(null);
	const productItemClasses = [s.productItem, 'u_productItem'];
	const path = canonical ? translate(setup, content, 'canonicalUri') : `${currentPath}/${content.uri}`;
	const canonicalCategory = rootUrl(content.canonicalUri);
	const isReSkate = content.canonicalUri.indexOf('re-skate') !== -1;
	const isSelected = productItemExpanded.indexOf(`${currentView}_${itemIndex}`) !== -1;
	let soldOut = !content.inStock;
	let badges = [];
	let newSet = false;
	let saleSet = false;
	let tags = [];

	if (!content || content.productHidden) return null;

	const triggerUpdateQuickShopHeight = () => {
		const updatedQuickShopHeight = productItemExpanded.indexOf(`${currentView}_${itemIndex}`) !== -1 && purchaseContainer.current ? `${height(purchaseContainer.current, 'scrollHeight')}px` : 0;
		updateQuickShopHeight(updatedQuickShopHeight);
	};

	useEffect(() => {
		toggleLazyLoad(true);
		return () => clearTimeout(timerRef.current);
	}, []);

	useEffect(() => {
		triggerUpdateQuickShopHeight();
	}, [productItemExpanded]);

	const productItemSelected = (e) => {
		const size = e.target.dataset.size;
		props.preSelectSize(size);
		selectAnalytics(content, setup, removeLanguagePrefix(currentView), itemIndex);
		props.updateListItemCache(content.groupId, removeLanguagePrefix(currentView));
	};

	const toggleSizesHandler = (e) => {
		if (productLayout === 'compact') return;
		e.preventDefault();
		const rect = e.target.getBoundingClientRect();
		const direction = rect.left < window.innerWidth / 2 ? 'left' : 'right';
		let adjacentCard = `${currentView}_${itemIndex + (direction === 'right' ? -1 : 1)}`;
		if (!document.querySelector(`#${adjacentCard}`)) adjacentCard = `${currentView}_${itemIndex + (direction === 'right' ? -2 : 2)}`;
		if (!document.querySelector(`#${adjacentCard}`)) adjacentCard = `${currentView}_${itemIndex}`;
		const $adjacentCard = document.querySelector(`#${adjacentCard} .${s.productItem_link}`);
		const $currentCard = document.querySelector(`#${currentView}_${itemIndex} .${s.productItem_link}`);

		setCardHeight([adjacentCard, height($adjacentCard)]);
		toggleProductItem(`${currentView}_${itemIndex}`, direction);

		$currentCard.style.height = `${height($currentCard)}px`;
		$adjacentCard.style.height = `${height($adjacentCard)}px`;
		document.querySelector(`#${adjacentCard}`).style.alignSelf = 'flex-start';
	};

	useEffect(() => {
		if (!isSelected && cardHeight[0] !== '') {
			timerRef.current = setTimeout(() => {
				const $adjacentCard = document.querySelector(`#${cardHeight[0]} .${s.productItem_link}`);
				const $currentCard = document.querySelector(`#${currentView}_${itemIndex} .${s.productItem_link}`);
				if ($adjacentCard) {
					$adjacentCard.style.height = '';
					document.querySelector(`#${cardHeight[0]}`).style.alignSelf = '';
				}
				if ($currentCard) $currentCard.style.height = '';
				setCardHeight(['', 'auto']);
			}, 300);
		}
	}, [isSelected]);

	let salePercent = '0%';
	if (content.price.showAsOnSale) {
		productItemClasses.push(s.productItem__sale);
		salePercent = `${Math.round(content.price.discountPercent || ((content.price.priceReductionAsNumber / content.price.priceBeforeDiscountAsNumber) * 100))}%`;
	}
	if (content.price.newProduct) productItemClasses.push(s.productItem__new);
	if (variation) productItemClasses.push(s[`productItem__${variation}`]);
	if (expandedDirection) productItemClasses.push(s[`productItem__${expandedDirection}`]);
	if (isSelected) productItemClasses.push(s.productItem__expanded);

	if (content.badges) {
		badges = Object.keys(content.badges).filter((b) => {
			if (parseInt(content.badges[b].is_new, 10) && !content.price.newProduct) return false;
			if ((parseInt(content.badges[b].is_sale, 10) && !content.price.showAsOnSale) || isReSkate) return false;
			if (content.badges[b].incPricelist && content.badges[b].incPricelist.split(/\s*,\s*/).indexOf(setup.pricelist) === -1) return false;
			if (content.badges[b].incMarket && content.badges[b].incMarket.split(/\s*,\s*/).indexOf(setup.market) === -1) return false;
			if (content.badges[b].incCountry && content.badges[b].incCountry.split(/\s*,\s*/).indexOf(setup.country) === -1) return false;
			if (!content.badges[b]?.image) return false;
			return true;
		}).map((badge) => {
			if (parseInt(content.badges[badge].is_new, 10) && content.price.newProduct) newSet = true;
			if (parseInt(content.badges[badge].is_sale, 10) && content.price.showAsOnSale) saleSet = true;
			return (
				<span key={badge} className={[s.productItem_badge, s.productItem_badge__svg].join(' ')}>
					<img src={content.badges[badge].image.url} alt="" />
				</span>
			);
		});
	}

	if (content.product_tags) {
		tags = Object.keys(content.product_tags).filter((t) => {
			if (parseInt(content.product_tags[t].is_new, 10) && !content.price.newProduct) return false;
			if (parseInt(content.product_tags[t].is_sale, 10) && !content.price.showAsOnSale) return false;
			if (content.product_tags[t].incPricelist && content.product_tags[t].incPricelist.split(/\s*,\s*/).indexOf(setup.pricelist) === -1) return false;
			if (content.product_tags[t].incMarket && content.product_tags[t].incMarket.split(/\s*,\s*/).indexOf(setup.market) === -1) return false;
			if (content.product_tags[t].incCountry && content.product_tags[t].incCountry.split(/\s*,\s*/).indexOf(setup.country) === -1) return false;
			return true;
		}).sort((a, b) => content.product_tags[a].tag.localeCompare(content.product_tags[b].tag)).map((t) => {
			const tagParts = content.product_tags[t].tag.split(' ');
			if (tagParts.indexOf('%') !== -1 || tagParts.indexOf('-%') !== -1) {
				saleSet = true;
				return <span key={`tag-${t}`} className={s.productItem_tag} style={{ backgroundColor: content.product_tags[t].listingColor || '#FFF', color: blackOrWhite(content.product_tags[t].listingColor || '#FFF', 1.05) }}>{ content.product_tags[t].tag.replace(/(-)?%/, `$1${salePercent}`) }</span>;
			}
			return <span key={`tag-${t}`} className={s.productItem_tag} style={{ backgroundColor: content.product_tags[t].listingColor || '#FFF', color: blackOrWhite(content.product_tags[t].listingColor || '#FFF', 1.05) }}>{ content.product_tags[t].tag }</span>;
		});
	}

	let sizes = content.sizes;
	const cache = productCache?.[setup.market]?.[setup.pricelist]?.products;
	if (content.relatedSizes && cache) {
		content.relatedSizes.forEach((size) => {
			if (cache[size]) sizes = [...sizes, ...cache[size].sizes];
		});
	}
	let priceRange = {};
	if (content.relatedSizes) {
		const sizeRelations = [content.groupId, ...content.relatedSizes];
		priceRange = sizeRelations
			.filter((p) => cache[p])
			.map((p) => [p, cache[p].price])
			.sort((a, b) => (a[1].priceAsNumber >= b[1].priceAsNumber ? 1 : -1))
			.reduce((acc, p) => [...acc, { id: p[0], price: p[1].price, priceBeforeDiscount: p[1].priceBeforeDiscount }], []);

		if (soldOut) soldOut = sizeRelations.every((rP) => cache?.[rP]?.inStock === 0);
	}

	if (!soldOut && content.price.newProduct && !newSet) {
		badges.unshift(<span key="new-badge" className={[s.productItem_badge, s.productItem_badge__text].join(' ')}>{ translate(setup, 'new') }</span>);
	} else if (!soldOut && content.price.showAsOnSale && !saleSet) {
		badges.unshift(<span key="sale-badge" className={[s.productItem_badge, s.productItem_badge__text].join(' ')}>{ `-${salePercent}` }</span>);
	}
	if (soldOut) productItemClasses.push(s.productItem__soldOut);

	const features = (content.features && Object.keys(content.features).reduce((acc, item) => {
		const f = content.features[item];
		if (!acc[f.featureId]) acc[f.featureId] = [];
		return { ...acc, [f.featureId]: [...acc[f.featureId], f] };
	}, {})) || {};

	return (
		<div id={`${currentView}_${itemIndex}`} className={productItemClasses.join(' ')} data-product-id={content.groupId} data-product-index={itemIndex}>
			<Link className={s.productItem_link} to={prefix(setup, path)} onClick={productItemSelected}>
				<div className={[s.productItem_tags, (badges.length === 0 ? s.productItem_tags__right : null)].join(' ')}>
					{ tags }
				</div>
				<div className={s.productItem_badges}>
					{ badges }
				</div>
				<div className={s.productItem_images}>
					{
						content.image.slice(0, 1).map((image) => <LazyImage key={image.slice(image.lastIndexOf('/') + 1)} {...productImageSizeTranslation('4', image)} alt={`${content.brand} ${content.name}`} />)
					}
				</div>
				<div className={s.productItem_text}>
					<span className={s.productItem_text_name}>{ content.name }</span>
					{
						features.metaTitle && (
							<span className={s.productItem_text_category}>{features.metaTitle.map((mt) => mt.feature).join(' ')}</span>
						)
					}
				</div>
				<div className={s.productItem_price}>
					<Price priceRange={priceRange} price={content.price} soldOut={soldOut ? translate(setup, 'soldOut') : null} isProductItem />
				</div>
			</Link>
			{
				variation !== 'minorProduct' && (
					<div className={s.productItem_actions}>
						{
							sizes.length > 1 && (
								<Button variantClasses={['button__quickShopMinor', (productItemExpanded.indexOf(`${currentView}_${itemIndex}`) !== -1 ? 'button__quickShopMinor__selected' : null)]} type="link" location={prefix(setup, path)} clickEventHandler={toggleSizesHandler}>
									<span>{ translate(setup, 'availableSizes') }</span>
								</Button>
							)
						}
					</div>
				)
			}
			{
				variation !== 'minorProduct' && sizes.length > 1 && lazyLoaded && (
					<div className={s.productItem_quickShop} style={{ height: quickShopHeight }}>
						<div className={s.productItem_quickShop_container} ref={purchaseContainer}>
							{ sizes.length > 0 && <Purchase plp sizes={sizes} setup={setup} currentPath={currentPath} productId={content.groupId} relatedProducts={{ size: content.relatedSizes }} soldOut={soldOut ? translate(setup, 'soldOut') : false} triggerUpdateQuickShopHeight={triggerUpdateQuickShopHeight} /> }
							<button className={s.productItem_quickShop_container_close} type="button" onClick={toggleSizesHandler}>
								<svg xmlns="http://www.w3.org/2000/svg" width="20" height="13" viewBox="0 0 20 13"><polyline fill="none" stroke="#979797" strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" points="2.842 2.74 10.342 10.24 17.842 2.74" /></svg>
							</button>
						</div>
					</div>
				)
			}
		</div>
	);
};

const mapState = ({ environment, productCache }, { content, id, currentPath, variation, ...props }) => ({
	setup: environment.setup,
	productCache,
	content,
	id,
	currentPath,
	variation,
	...props,
});

export default hot(module)(connect(mapState, { preSelectSize, updateListItemCache })(ProductItem));
